/**
 * Hub Authentication Service for Panda-Doc (Quote App)
 * 
 * Handles token exchange with the AGI Customer Portal (Hub).
 * Pattern matches rma-portal's auth flow:
 * 1. Hub opens panda-doc with ?access_token=xxx&token=xxx
 * 2. Token stored in localStorage
 * 3. Token used for backend API calls
 * 4. On exit, redirect back to hub
 */

const HUB_URL = process.env.REACT_APP_HUB_URL || 'https://customer-portal-447.pages.dev';
const HUB_TOKEN_KEY = 'agi_hub_access_token';

/**
 * Initialize auth from hub URL params.
 * Call this once on app mount (before routing).
 * 
 * @returns {{ token: string|null, fromHub: boolean }}
 */
export function initHubAuth() {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Check for token in URL (passed by customer portal)
  const token = params.get('access_token') || params.get('token');

  if (token) {
    // Store token and mark as hub-launched
    localStorage.setItem(HUB_TOKEN_KEY, token);
    sessionStorage.setItem('launched_from_hub', 'true');

    // Clean URL to remove sensitive tokens from browser history
    const cleanUrl = url.origin + url.pathname;
    window.history.replaceState({}, document.title, cleanUrl);

    return { token, fromHub: true };
  }

  // Check for previously stored token (page refresh scenario)
  const storedToken = localStorage.getItem(HUB_TOKEN_KEY);
  const fromHub = sessionStorage.getItem('launched_from_hub') === 'true';

  return { token: storedToken, fromHub };
}

/**
 * Get the current hub token (for API calls).
 * Returns the hub token if launched from hub, otherwise null.
 */
export function getHubToken() {
  return localStorage.getItem(HUB_TOKEN_KEY);
}

/**
 * Check if the app was launched from the customer portal hub.
 */
export function isLaunchedFromHub() {
  return sessionStorage.getItem('launched_from_hub') === 'true';
}

/**
 * Exit the app — clear tokens and redirect back to hub.
 * If not launched from hub, redirects to login page.
 */
export function exitApp() {
  // Clear stored auth data
  localStorage.removeItem(HUB_TOKEN_KEY);
  localStorage.removeItem('user');
  localStorage.removeItem('profile');
  localStorage.removeItem('authContext');
  sessionStorage.removeItem('launched_from_hub');

  // Always redirect to customer portal
  window.location.replace(HUB_URL);
}

/**
 * Get the hub URL for "Back to Portal" links.
 */
export function getHubUrl() {
  return HUB_URL;
}
